import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import Success from './Success';
import RetrieveLicense from './RetrieveLicense';
import Support from './Support';
import './App.css';

// Load Stripe with your publishable key from environment variable
const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
    'pk_live_51PkCxzCdf82O5H1WDX5jmKPdijNUzXue0K1PRGT2VNYrlnpTz8LW0DpSFXXG9PcAPUQQrUBfjE3NWnus0acv7o1t00VyrfPgT6'
);

function App() {
    const [loading, setLoading] = useState(false);

    // Check if we're on the success page
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
        return <Success />;
    }

    // Check if we're on the retrieve license page
    const path = window.location.pathname;
    if (path === '/retrieve' || path === '/retrieve-license') {
        return <RetrieveLicense />;
    }

    // Check if we're on the support page
    if (path === '/support') {
        return <Support />;
    }

    const handlePurchase = async (productId, productName) => {
        setLoading(true);

        try {
            const stripe = await stripePromise;

            // Create checkout session using product ID (Stripe handles multi-currency pricing)
            const response = await fetch('/api/create-checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    product_id: productId,
                    product_name: productName,
                    success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
                    cancel_url: `${window.location.origin}/cancel`,
                }),
            });

            const data = await response.json();

            console.log('Server response:', data);
            console.log('Response status:', response.status);

            if (!response.ok || data.error) {
                alert(`Error: ${data.error || 'Server returned an error'}`);
                setLoading(false);
                return;
            }

            if (!data.session_id) {
                alert('No session ID returned from server');
                console.error('Response data:', data);
                setLoading(false);
                return;
            }

            // Redirect to Stripe Checkout
            const result = await stripe.redirectToCheckout({
                sessionId: data.session_id,
            });

            if (result.error) {
                alert(result.error.message);
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="App">
            <header className="App-header">
                <div className="header-content">
                    <img src="/logo.png" alt="BatchMark PDF Logo" className="logo" />
                    <div>
                        <h1>BatchMark PDF</h1>
                        <p className="tagline">Professional batch PDF search, highlight, and annotation tool</p>
                    </div>
                </div>
            </header>

            <main className="main-content">
                <section className="features">
                    <h2>Features</h2>
                    <div className="feature-grid">
                        <div className="feature">
                            <span className="icon">🔍</span>
                            <h3>Powerful Search</h3>
                            <p>Search across multiple PDFs with regex support</p>
                        </div>
                        <div className="feature">
                            <span className="icon">✨</span>
                            <h3>Smart Highlighting</h3>
                            <p>Highlight matches with custom colors</p>
                        </div>
                        <div className="feature">
                            <span className="icon">⚡</span>
                            <h3>Batch Processing</h3>
                            <p>Process entire folders in parallel</p>
                        </div>
                        <div className="feature">
                            <span className="icon">💾</span>
                            <h3>Save & Export</h3>
                            <p>Export highlighted PDFs instantly</p>
                        </div>
                    </div>
                </section>

                <section className="pricing">
                    <h2>Get Started</h2>
                    <div className="pricing-grid">
                        <div className="pricing-card">
                            <div className="pricing-header">
                                <h3>Personal License</h3>
                                <div className="price">
                                    <span className="currency">$</span>
                                    <span className="amount">99</span>
                                    <span className="period">CAD</span>
                                </div>
                            </div>
                            <ul className="features-list">
                                <li>✅ Lifetime license</li>
                                <li>✅ 1 year of updates</li>
                                <li>✅ All features included</li>
                                <li>✅ Regex search</li>
                                <li>✅ Batch processing</li>
                                <li>✅ Custom colors</li>
                                <li>✅ For personal use</li>
                            </ul>
                            <button
                                className="buy-button"
                                onClick={() => handlePurchase('prod_TNB8qz8lDP586Q', 'Personal License')}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Buy Personal License'}
                            </button>
                        </div>

                        <div className="pricing-card featured">
                            <div className="badge">Most Popular</div>
                            <div className="pricing-header">
                                <h3>Business License</h3>
                                <div className="price">
                                    <span className="currency">$</span>
                                    <span className="amount">250</span>
                                    <span className="period">CAD</span>
                                </div>
                            </div>
                            <ul className="features-list">
                                <li>✅ Lifetime license</li>
                                <li>✅ 1 year of updates</li>
                                <li>✅ All features included</li>
                                <li>✅ Regex search</li>
                                <li>✅ Batch processing</li>
                                <li>✅ Custom colors</li>
                                <li>✅ For commercial use</li>
                                <li>✅ Priority support</li>
                                <li>✅ Tax compliance included</li>
                            </ul>
                            <button
                                className="buy-button"
                                onClick={() => handlePurchase('prod_TNB8fbpIC0ym3L', 'Business License')}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Buy Business License'}
                            </button>
                        </div>
                    </div>
                </section>

                <section className="demo">
                    <h2>Download BatchMark PDF</h2>
                    <p>Get started with BatchMark PDF - Professional batch PDF search, highlight, and annotation tool</p>
                    <div className="download-buttons">
                        <a href="/downloads/BatchMarkPDF-Windows-Setup-v0.1.0.exe" className="download-btn">
                            <span className="icon">🪟</span>
                            <div>
                                <div>Windows Installer</div>
                                <small style={{ fontSize: '0.8rem', opacity: 0.7 }}>v0.1.0 • 9.8 MB</small>
                            </div>
                        </a>
                        <a href="#" className="download-btn disabled" onClick={(e) => e.preventDefault()}>
                            <span className="icon">🍎</span>
                            macOS
                            <span className="coming-soon">Coming Soon</span>
                        </a>
                        <a href="#" className="download-btn disabled" onClick={(e) => e.preventDefault()}>
                            <span className="icon">🐧</span>
                            Linux
                            <span className="coming-soon">Coming Soon</span>
                        </a>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#95a5a6', marginTop: '1rem' }}>
                        ✅ Windows 10/11 (64-bit) • Includes PDFium library • Free to try
                    </p>
                </section>
            </main>

            <footer className="footer">
                <p>&copy; 2025 BatchMark PDF. All rights reserved.</p>
                <p>
                    <a href="/retrieve" style={{ color: '#ffffffff', textDecoration: 'none', marginRight: '1rem' }}>
                        🔑 Retrieve License
                    </a>
                    |
                    <a href="/support" style={{ color: '#ffffffff', textDecoration: 'none', margin: '0 1rem' }}>
                        📧 Support
                    </a>
                </p>
            </footer>
        </div>
    );
}

export default App;
